import { HID_POS_USAGE_PAGE, NEWLAND_VENDOR_ID } from 'models/QRCodeScannerDevice/constants';
import { ScannerConnectionFailureReason, ScannerLookup } from 'models/QRCodeScannerDevice/types';

export const describeDevice = ({ productName, vendorId, productId }: HIDDevice) => `${productName} (${vendorId}/${productId})`;

// HIDDevice exposes its data through prototype getters: copy them explicitly so they survive JSON serialization
export const serializeDevices = (devices: HIDDevice[]): string => JSON.stringify(
  devices.map(({ opened, vendorId, productId, productName, collections }) => ({ opened, vendorId, productId, productName, collections })),
  null,
  2,
);

const hasHidPosCollection = ({ collections }: HIDDevice) => (collections ?? []).some(({ usagePage }) => usagePage === HID_POS_USAGE_PAGE);

/**
 * A single scanner may expose several HID interfaces (HID POS, vendor configuration, ...).
 * When the vendor appears more than once, only the HID POS interface sends the decoded data.
 */
export const findScanner = (devices: HIDDevice[]): ScannerLookup => {
  if (devices.length <= 0) {
    return { reason: ScannerConnectionFailureReason.NO_ALLOWED_HID_DEVICE };
  }

  const vendorDevices = devices.filter(({ vendorId }) => vendorId === NEWLAND_VENDOR_ID);
  if (vendorDevices.length <= 0) {
    return { reason: ScannerConnectionFailureReason.SCANNER_VENDOR_NOT_FOUND };
  }

  if (vendorDevices.length === 1) {
    return { device: vendorDevices[0] };
  }

  const hidPosDevice = vendorDevices.find(hasHidPosCollection);
  return hidPosDevice ? { device: hidPosDevice } : { reason: ScannerConnectionFailureReason.NO_HID_POS_INTERFACE };
};

// For side work (logs, reports, cleanup) that must never break the scanner
export const runSafely = (action: () => void): void => {
  try {
    action();
  } catch {
    // Ignored on purpose: the connection loop matters more
  }
};
